import { CompRef } from '@wix/document-services-types';
import { ManifestServiceFlags } from '../../ManifestServiceFlags';
import { ManifestServiceStats } from '../../ManifestServiceStats';
import { ManifestChangeDetection } from '../../ManifestChangeDetection';
import { IVirtualManifest } from '../../builders';
import { MethodCacheStrategy } from '../MethodCacheStrategy';
import { ManifestAPICache } from '../ManifestAPICache';

import {
  EditorPointer,
  EditorPointerServiceAPI,
} from '@wix/editor-document-logic-api';

interface ElementDescriptor {
  kind: 'method';
  key: string;
  placement: string;
  descriptor: PropertyDescriptor;
}

export { MethodCacheStrategy };

interface IConsumerInstance {
  manifestServiceFlags?: ManifestServiceFlags;
  manifestServiceStats?: ManifestServiceStats;
  manifestChangeDetection?: ManifestChangeDetection;
  virtualManifest?: IVirtualManifest;
  editorPointerAPI?: EditorPointerServiceAPI;
}

// For singleton classes without virtualManifest
const instanceScopeMap = new WeakMap<object, string>();
let instanceScopeCounter = 0;

// For class identity (minification-safe, unlike constructor.name)
const classIdMap = new Map<Function, number>();
let classIdCounter = 0;

const deriveCacheScope = (_self: IConsumerInstance): string => {
  if (_self.virtualManifest) {
    const vm = _self.virtualManifest;
    const cp = vm.childPath;
    return `${vm.virtualManifestId}__${cp?.length ? cp.join('.') : 'root'}`;
  }
  if (!instanceScopeMap.has(_self)) {
    instanceScopeMap.set(_self, `inst_${++instanceScopeCounter}`);
  }
  return instanceScopeMap.get(_self)!;
};

const getClassId = (ctor: Function) => {
  if (!classIdMap.has(ctor)) {
    classIdMap.set(ctor, ++classIdCounter);
  }
  return classIdMap.get(ctor)!;
};

let manifestAPICache: ManifestAPICache | null = null;

export function CacheMethodResponse<
  TArgs extends any[] = unknown[],
  TResponse = unknown,
>(options: {
  namespace?: string;
  buildCacheKey?: (self: IConsumerInstance, ...args: TArgs) => string | null;
  cacheStrategy: MethodCacheStrategy;
  extractEditorPointers?: (
    self: IConsumerInstance,
    ...args: TArgs
  ) => EditorPointer<CompRef>[];
  transformResponseForStatsCollecting?: (response: TResponse) => unknown;
}) {
  const createDecorator = (
    originalMethod: (...args: TArgs) => any,
    propertyKey: string,
  ) => {
    return function (this: IConsumerInstance, ...args: TArgs) {
      const start = performance.now();
      const _self = this;

      const extractEditorPointers = () => {
        if (options.extractEditorPointers) {
          return options.extractEditorPointers(_self, ...args) ?? [];
        }

        if (_self.virtualManifest?.tree?.editorPointer) {
          return [_self.virtualManifest.tree.editorPointer];
        }

        return (
          _self.virtualManifest?.manifests.map((x) => x.editorPointer) ?? []
        );
      };

      const generateStatsMethodKey = () => {
        const methodKey = options?.cacheStrategy
          ? `${propertyKey}__${options.cacheStrategy}`
          : propertyKey;

        return options?.namespace
          ? `${options.namespace}.${methodKey}`
          : methodKey;
      };

      const isCacheMethodUnavailable =
        [
          MethodCacheStrategy.StylesUpdate,
          MethodCacheStrategy.SiteUpdate,
          MethodCacheStrategy.DataUpdate,
          MethodCacheStrategy.DataUpdate,
          MethodCacheStrategy.PresetUpdate,
          MethodCacheStrategy.PresetOrStateUpdate,
        ].includes(options.cacheStrategy) && !_self.manifestChangeDetection;

      const methodKey = generateStatsMethodKey();

      let cacheKey: string | null = null;

      /**
       * try/catch to ensure there are no cycle structures in the args,
       * so JSON.stringify works.
       * otherwise we do not cache
       */
      try {
        cacheKey = options.buildCacheKey
          ? options.buildCacheKey(_self, ...args)
          : JSON.stringify({ propertyKey, args });
      } catch (e) {
        cacheKey = null;
      }

      if (
        _self.manifestServiceFlags?.isPlatformCacheDisabled ||
        // TODO:
        // _self.manifestChangeDetection?.isInTransaction() ||
        !cacheKey ||
        isCacheMethodUnavailable
      ) {
        _self.manifestServiceStats?.method(
          methodKey,
          false,
          performance.now() - start,
        );

        return originalMethod.apply(_self, args);
      }

      const scope = deriveCacheScope(_self);
      const classId = getClassId(_self.constructor);
      const globalKey = `${scope}::${classId}::${cacheKey}`;

      if (
        !manifestAPICache &&
        _self.manifestChangeDetection &&
        _self.editorPointerAPI
      ) {
        manifestAPICache = new ManifestAPICache(
          _self.editorPointerAPI,
          _self.manifestChangeDetection,
          _self.manifestServiceStats,
        );
      }

      if (manifestAPICache?.has(globalKey)) {
        _self.manifestServiceStats?.method(
          methodKey,
          true,
          performance.now() - start,
        );

        return manifestAPICache.get(globalKey);
      }

      const result = originalMethod.apply(_self, args);

      manifestAPICache?.set(globalKey, {
        value: result,
        editorPointers: extractEditorPointers(),
        strategy: options.cacheStrategy,
        hasContainerAction:
          !!_self.virtualManifest?.tree?.manifest?.editorElement?.actions
            ?.container,
        transformResponse: options.transformResponseForStatsCollecting as
          | ((r: unknown) => unknown)
          | undefined,
      });

      _self.manifestServiceStats?.cache(
        'CacheMethodResponse',
        globalKey,
        'created',
        options?.transformResponseForStatsCollecting
          ? options?.transformResponseForStatsCollecting(result)
          : result,
      );

      _self.manifestServiceStats?.method(
        methodKey,
        false,
        performance.now() - start,
      );

      return result;
    };
  };

  return function (elementDescriptor: ElementDescriptor) {
    if (!elementDescriptor?.descriptor?.value) {
      return elementDescriptor;
    }

    return {
      ...elementDescriptor,
      descriptor: {
        ...elementDescriptor.descriptor,
        value: createDecorator(
          elementDescriptor.descriptor.value,
          // TODO: what if key is Symbol?
          elementDescriptor.key,
        ),
      },
    };
    // TODO: type miss match in TS (yoshi) configuration
  } as any;
}
